/**
 * Which chart series are shown, and in which colour, keyed by the series' identity instead of
 * its position. A paged chart (◀ ▶) rebuilds its series from each page's rows, so the same
 * position can hold a different series on the next page; keyed state keeps the user's choice
 * and the colours with the series themselves.
 */

/** What identifies a series: the pivot key when there is one, else its legend name. */
export interface SeriesIdentity {
  key?: string | null;
  name: string;
}

/** Per-chart rules for which series start out visible and how the legend is ordered. */
export interface ChartSeriesDefaults {
  /** Series visible until the user toggles them. Without it, the first ten are. */
  isDefaultVisible?: (series: SeriesIdentity) => boolean;
  /** Label of the legend button that goes back to the defaults, e.g. "Main sensors only". */
  defaultLabel?: string;
  /** Order the legend by name (natural order) so it does not reshuffle between pages. */
  sortByName?: boolean;
}

/** Series shown by default when a chart sets no rule of its own. */
export const DEFAULT_VISIBLE_COUNT = 10;
/** Most toggles remembered per chart; the oldest are dropped first. */
const MAX_REMEMBERED = 500;

export function seriesIdOf(series: SeriesIdentity): string {
  return series.key ?? series.name;
}

const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** The series in legend order: as given, or by name in natural order ("…2" before "…10"). */
export function orderSeries<T extends SeriesIdentity>(series: readonly T[], sortByName: boolean): T[] {
  if (!sortByName) return [...series];
  return [...series].sort((a, b) => naturalCollator.compare(a.name, b.name));
}

/**
 * Palette slots by series id for the series on screen (`ids`). Ids already known keep their slot;
 * a new id takes the lowest slot not used by another series on screen — slots of series seen
 * earlier but not shown now (another kiosk, another page) are free to reuse, so the colours on
 * screen stay distinct as long as the palette lasts. Returns the updated map of all known ids.
 */
export function assignColorSlots(
  ids: readonly string[],
  known: Readonly<Record<string, number>>,
  paletteSize: number,
): Record<string, number> {
  const next: Record<string, number> = { ...known };
  const used = new Set<number>();
  for (const id of ids) if (next[id] !== undefined) used.add(next[id]!);
  let candidate = 0;
  let wrapped = 0;
  for (const id of ids) {
    if (next[id] !== undefined) continue;
    while (used.has(candidate) && candidate < paletteSize) candidate++;
    const slot = candidate < paletteSize ? candidate : wrapped++ % paletteSize;
    next[id] = slot;
    used.add(slot);
  }
  return next;
}

/** Override key set by "Show all" / "Hide all": applies to every series without its own toggle,
 *  including series on pages not seen yet. Cannot clash with a series id (NUL prefix). */
export const ALL_SERIES = "\u0000all";

/** Whether a series is shown: its own toggle, else "Show/Hide all", else the chart's default rule. */
export function isSeriesVisible(
  series: SeriesIdentity,
  rank: number,
  overrides: Readonly<Record<string, boolean>>,
  defaults?: ChartSeriesDefaults,
): boolean {
  const own = overrides[seriesIdOf(series)] ?? overrides[ALL_SERIES];
  if (own !== undefined) return own;
  return defaults?.isDefaultVisible ? defaults.isDefaultVisible(series) : rank < DEFAULT_VISIBLE_COUNT;
}

/** Which preset the legend's selection matches; "custom" once the user toggled single series
 *  (or anything else a preset does not produce, e.g. an "everything hidden" saved earlier). */
export type SeriesSelectionMode = "default" | "all" | "custom";

export function selectionModeOf(overrides: Readonly<Record<string, boolean>>): SeriesSelectionMode {
  const keys = Object.keys(overrides);
  if (keys.length === 0) return "default";
  if (keys.length === 1 && keys[0] === ALL_SERIES && overrides[ALL_SERIES]) return "all";
  return "custom";
}

/** The overrides that select a preset. */
export function overridesForMode(mode: Exclude<SeriesSelectionMode, "custom">): Record<string, boolean> {
  return mode === "all" ? { [ALL_SERIES]: true } : {};
}

/** The overrides with `id` set, keeping only the newest MAX_REMEMBERED entries. */
export function withOverride(
  overrides: Readonly<Record<string, boolean>>,
  id: string,
  visible: boolean,
): Record<string, boolean> {
  const { [id]: _drop, ...rest } = overrides;
  const entries = Object.entries(rest);
  const kept = entries.length >= MAX_REMEMBERED ? entries.slice(entries.length - MAX_REMEMBERED + 1) : entries;
  return { ...Object.fromEntries(kept), [id]: visible };
}

/** What is remembered per chart in this browser. */
export interface StoredSeriesState {
  /** Series the user showed (true) or hid (false), by id. */
  visible: Record<string, boolean>;
  /** Palette slot per series id, so a series keeps its colour across pages and visits. */
  colors: Record<string, number>;
}

const storageKeyOf = (chartKey: string) => `webadmincc-chart-series.${chartKey.toLowerCase()}`;

export function loadSeriesState(chartKey: string | undefined): StoredSeriesState {
  const empty: StoredSeriesState = { visible: {}, colors: {} };
  if (!chartKey) return empty;
  try {
    const raw = localStorage.getItem(storageKeyOf(chartKey));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<StoredSeriesState>;
    return {
      visible: parsed.visible && typeof parsed.visible === "object" ? parsed.visible : {},
      colors: parsed.colors && typeof parsed.colors === "object" ? parsed.colors : {},
    };
  } catch {
    return empty;
  }
}

/** Most remembered colours per chart; the oldest are dropped first. */
const MAX_REMEMBERED_COLORS = 1000;

export function saveSeriesState(chartKey: string | undefined, state: StoredSeriesState): void {
  if (!chartKey) return;
  const colorEntries = Object.entries(state.colors);
  const colors = colorEntries.length > MAX_REMEMBERED_COLORS
    ? Object.fromEntries(colorEntries.slice(colorEntries.length - MAX_REMEMBERED_COLORS))
    : state.colors;
  try {
    localStorage.setItem(storageKeyOf(chartKey), JSON.stringify({ visible: state.visible, colors }));
  } catch {
    /* storage full or blocked — the choice just isn't remembered */
  }
}
